import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Menu, 
  X, 
  ChevronDown,
  Ruler,
  Palette,
  Wrench,
  ArrowRight,
  Check,
  MessageCircle,
  Expand
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import { Card, CardContent } from "./components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = ({ activeSection }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#par-mums", label: "Par mums" },
    { href: "#pakalpojumi", label: "Pakalpojumi" },
    { href: "#projekti", label: "Projekti" },
    { href: "#kontakti", label: "Kontakti" },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsOpen(false);
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <a 
          href="#hero" 
          onClick={(e) => scrollToSection(e, "#hero")}
          className="flex items-center gap-3"
          data-testid="logo-link"
        >
          <div className="flex flex-col leading-none">
            <span className="text-3xl md:text-4xl font-serif font-bold tracking-wide gold-gradient-text">
              KR
            </span>
            <span className="text-xs md:text-sm font-medium tracking-[0.35em] text-[var(--gold-dark)] uppercase">
              Planning
            </span>
          </div>
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => scrollToSection(e, link.href)}
              data-testid={`nav-${link.href.replace('#', '')}`}
              className={`relative text-sm font-medium tracking-wide uppercase transition-colors hover:text-[var(--gold)] ${
                activeSection === link.href.replace('#', '') 
                  ? "text-[var(--gold)]" 
                  : "text-[var(--white-muted)]"
              }`}
            >
              {link.label}
              <span className={`absolute -bottom-1 left-0 h-0.5 bg-[var(--gold)] transition-all duration-300 ${
                activeSection === link.href.replace('#', '') ? "w-full" : "w-0"
              }`} />
            </a>
          ))}
          <a
            href="tel:+37120310517"
            data-testid="nav-phone"
            className="flex items-center gap-2 px-4 py-2 gold-border-gradient rounded-full text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--black)] transition-all duration-300"
          >
            <Phone size={16} />
            <span className="text-sm font-medium">20 310 517</span>
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-[var(--white)] p-2"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      <div
        className={`md:hidden absolute top-full left-0 right-0 glass transition-all duration-300 ${
          isOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-4">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => scrollToSection(e, link.href)}
              data-testid={`mobile-nav-${link.href.replace('#', '')}`}
              className="text-lg font-medium text-[var(--white-muted)] hover:text-[var(--gold)] transition-colors"
            >
              {link.label}
            </a>
          ))}
          <a
            href="tel:+37120310517"
            className="flex items-center gap-2 text-[var(--gold)] mt-2"
          >
            <Phone size={18} />
            <span>20 310 517</span>
          </a>
        </div>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1765767056681-9583b29007cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbHV4dXJ5JTIwaW50ZXJpb3IlMjBsaXZpbmclMjByb29tJTIwYmxhY2t8ZW58MHx8fHwxNzcxNDMzNTQzfDA&ixlib=rb-4.1.0&q=85"
          alt="Luxury interior"
          className="w-full h-full object-cover object-[70%_center] md:object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--black)]/85 via-[var(--black)]/70 to-[var(--black)]" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-12 md:px-6 text-center">
        <div className="animate-fade-in-up opacity-0">
          <p className="text-[var(--gold)] text-sm md:text-base uppercase tracking-[0.3em] mb-6">
            Interjera dizains & Projektu vadība
          </p>
        </div>
        
        <h1 className="animate-fade-in-up opacity-0 delay-100 text-4xl sm:text-5xl lg:text-7xl font-serif font-bold mb-6 leading-tight">
          Pārvēršam jūsu
          <span className="block gold-gradient-text">sapņus realitātē</span>
        </h1>
        
        <p className="animate-fade-in-up opacity-0 delay-200 text-base md:text-lg text-[var(--white-muted)] max-w-2xl mx-auto mb-10">
          Specializēti projektu pakalpojumi, iekšdarbu dizains un montāža ar 
          augstākās kvalitātes standartu
        </p>
        
        <div className="animate-fade-in-up opacity-0 delay-300 flex flex-col sm:flex-row gap-4 justify-center">
          <a href="#kontakti">
            <Button 
              data-testid="hero-cta-primary"
              className="bg-[var(--gold)] hover:bg-[var(--gold-light)] text-[var(--black)] font-semibold px-8 py-6 text-base rounded-full transition-all duration-300 hover:scale-105"
            >
              Sazināties ar mums
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </a>
          <a href="#pakalpojumi">
            <Button
              data-testid="hero-cta-secondary"
              variant="outline"
              className="border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--black)] font-semibold px-8 py-6 text-base rounded-full transition-all duration-300"
            >
              Mūsu pakalpojumi
            </Button>
          </a>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-float">
        <a href="#par-mums" className="flex flex-col items-center gap-2 text-[var(--gold)]">
          <span className="text-xs uppercase tracking-widest">Ritināt</span>
          <ChevronDown size={24} />
        </a>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section
      id="par-mums"
      data-testid="about-section"
      className="section-padding bg-[var(--black-light)]"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-2xl hover-lift">
              <img
                src="/projects/marupe-kitchen-1.png"
                alt="KR Planning interjers - moderna virtuve ar melniem skapjiem un zeltainiem akcentiem"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--black)]/60 to-transparent" />
            </div>
            {/* Decorative element */}
            <div className="absolute -bottom-6 -right-6 w-48 h-48 gold-border-gradient rounded-2xl -z-10" />
          </div>

          {/* Content */}
          <div>
            <p className="text-[var(--gold)] text-sm uppercase tracking-[0.2em] mb-4">
              Par mums
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-6 leading-tight">
              Mēs radām
              <span className="gold-gradient-text block">izcilus interjerus</span>
            </h2>
            <p className="text-[var(--white-muted)] text-base md:text-lg leading-relaxed mb-6">
              KR Planning ir pieredzējuša interjera dizaina un projektu vadības 
              kompānija, kas specializējas augstākās kvalitātes iekšdarbu izstrādē. 
              Mūsu komanda apvieno radošumu ar tehnisko izcilību, lai katrs projekts 
              pārsniegtu klienta cerības.
            </p>
            <p className="text-[var(--white-muted)] text-base md:text-lg leading-relaxed mb-8">
              No koncepcijas līdz realizācijai - mēs esam ar jums katrā solī, 
              nodrošinot bezrūpīgu un profesionālu pieredzi.
            </p>

            {/* Stats */}
            <div className="p-6 rounded-xl bg-[var(--black-lighter)] border border-[var(--gold)]/30">
              <p className="text-[var(--gold)] text-lg md:text-xl font-serif font-semibold mb-2">
                10+ gadu pieredze
              </p>
              <p className="text-[var(--white-muted)] text-sm md:text-base">
                Mūsu dizaineri un montāžnieki apvieno vairāk nekā desmit gadu profesionālo pieredzi interjera nozarē
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: <Palette className="w-8 h-8" />,
      title: "Interjera dizains",
      description: "Izstrādājam profesionālus interjera dizaina projektus dzīvokļiem un privātmājām Mārupē un Rīgā. Piedāvājam pilnu vannas istabas dizaina izstrādi ar santehnikas un tehnisko risinājumu plānošanu.",
      features: ["3D vizualizācijas", "Vannas istabas dizains", "Santehnikas plānošana", "Materiālu un apdares izvēle", "Mēbeļu un apgaismojuma risinājumi"],
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: "Iekšdarbi un montāža",
      description: "Veicam kvalitatīvus iekšdarbus un santehnikas montāžu Mārupē, Rīgā un Pierīgā. Strādājam ar augstas kvalitātes materiāliem un nodrošinām precīzu darbu izpildi atbilstoši projektam.",
      features: ["Vannas istabas remontu", "Santehnikas montāžu un pieslēgumus", "Kanalizācijas un ūdens sistēmu izbūvi", "Flīzēšanu", "Reģipša montāžu", "Grīdu ieklāšanu un sienu apdari"],
    },
    {
      icon: <Ruler className="w-8 h-8" />,
      title: "Projektu vadība",
      description: "Nodrošinām pilna cikla projektu vadību – no idejas un dizaina izstrādes līdz gatavam objektam. Organizējam materiālu iepirkumus, darbu koordinēšanu un kvalitātes kontroli.",
      features: ["Tāmju sastādīšanu", "Darbu plānošanu un grafika kontroli", "Apakšuzņēmēju koordinēšanu", "Budžeta uzraudzību", "Iepirkumu organizēšana un materiālu nodrošināšana"],
    },
  ];

  return (
    <section
      id="pakalpojumi"
      data-testid="services-section"
      className="section-padding bg-[var(--black)]"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[var(--gold)] text-sm uppercase tracking-[0.2em] mb-4">
            Pakalpojumi
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-6">
            Ko mēs <span className="gold-gradient-text">piedāvājam</span>
          </h2>
          <p className="text-[var(--white-muted)] text-sm md:text-lg max-w-2xl mx-auto px-4 md:px-0">
            Mūsu plašais pakalpojumu klāsts nodrošina visu nepieciešamo 
            jūsu sapņu interjera realizācijai
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <Card
              key={index}
              data-testid={`service-card-${index}`}
              className="bg-[var(--black-lighter)] border-[var(--gray-dark)] hover-lift group cursor-pointer overflow-hidden"
            >
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-xl bg-[var(--gold)]/10 flex items-center justify-center text-[var(--gold)] mb-6 group-hover:bg-[var(--gold)] group-hover:text-[var(--black)] transition-all duration-300">
                  {service.icon}
                </div>
                <h3 className="text-xl md:text-2xl font-serif font-semibold mb-4 text-[var(--white)]">
                  {service.title}
                </h3>
                <p className="text-[var(--gray-light)] text-sm md:text-base mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-[var(--white-muted)]">
                      <Check size={16} className="text-[var(--gold)] flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Projects Section
const ProjectsSection = () => {
  const [lightbox, setLightbox] = useState(null); // { projectIndex }

  const projects = [
    {
      title: "Eleganta vannas istaba",
      location: "Cēsis",
      category: "Interjera dizains & Iekšdarbi",
      description:
        "Pilna cikla vannas istabas remonts un interjera dizains privātmājā Cēsīs. Projektā realizēta marmora flīžu apdare sienām un grīdai, speciāli izgatavota ozolkoka vannas istabas mēbele ar granīta virsmu, bronzas toņa santehnika (jaucējkrāni, dušas sistēma) un brīvi stāvoša vanna. Darbu apjoms: dizaina izstrāde, santehnikas un kanalizācijas izbūve, flīzēšana, apgaismojuma plānošana un mēbeļu montāža.",
      keywords: "Vannas istabas remonts Cēsīs · Marmora apdare · Bronzas santehnika · Brīvi stāvoša vanna · Ozolkoka mēbeles",
      images: [
        "/projects/cesis-3.jpeg",
        "/projects/cesis-1.jpeg",
        "/projects/cesis-2.jpeg",
      ],
    },
    {
      title: "Moderna virtuve",
      location: "Mārupe",
      category: "Interjera dizains & Iekšdarbi",
      description:
        "Moderna virtuves interjera izstrāde un izbūve privātmājā Mārupē. Projektā izmantoti matēti melnie virtuves skapīši ar zeltainiem rokturiem, dabīga koka darba virsma, marmora aizmugures siena un iebūvēta Bosch sadzīves tehnika. Speciāli izveidots LED slēptais apgaismojums zem augšējiem skapīšiem un gar grīdu rada siltu, greznu atmosfēru. Darbu apjoms: virtuves dizains, mēbeļu projektēšana un montāža, elektroinstalācija un apgaismojums.",
      keywords: "Virtuves dizains Mārupē · Melna virtuve · Zelta akcenti · LED apgaismojums · Bosch iebūvējamā tehnika",
      images: [
        "/projects/marupe-kitchen-1.png",
      ],
    },
  ];

  const openLightbox = (projectIndex) => setLightbox({ projectIndex });
  const closeLightbox = useCallback(() => setLightbox(null), []);

  useEffect(() => {
    if (lightbox === null) {
      document.body.style.overflow = "";
      return;
    }
    document.body.style.overflow = "hidden";
    const handleKey = (e) => {
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, closeLightbox]);

  const activeProject = lightbox !== null ? projects[lightbox.projectIndex] : null;

  return (
    <section
      id="projekti"
      data-testid="projects-section"
      className="section-padding bg-[var(--black-light)]"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[var(--gold)] text-sm uppercase tracking-[0.2em] mb-4">
            Portfolio
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-6">
            Mūsu <span className="gold-gradient-text">projekti</span>
          </h2>
          <p className="text-[var(--white-muted)] text-base md:text-lg max-w-2xl mx-auto">
            Iepazīstieties ar mūsu veiksmīgākajiem darbiem un iedvesmojieties
            savam nākamajam projektam
          </p>
        </div>

        {/* Projects list */}
        <div className="space-y-20">
          {projects.map((project, pIdx) => (
            <div
              key={pIdx}
              data-testid={`project-featured-${pIdx}`}
              className="scroll-mt-24"
            >
              <div className="mb-8 text-center md:text-left">
                <p className="text-[var(--gold)] text-xs uppercase tracking-[0.3em] mb-3">
                  {project.category}
                </p>
                <h3 className="text-2xl md:text-4xl font-serif font-semibold text-[var(--white)] mb-3">
                  {project.title}
                  <span className="text-[var(--gold)]"> — {project.location}</span>
                </h3>
                <div className="flex items-center gap-2 text-[var(--gray-light)] text-sm justify-center md:justify-start">
                  <MapPin size={14} className="text-[var(--gold)]" />
                  <span>{project.location}, Latvija</span>
                </div>
              </div>

              {project.images.length >= 3 ? (
                /* 3+ images: 1 large + 2 stacked */
                <div className="grid md:grid-cols-3 gap-4 md:gap-6">
                  <div
                    data-testid={`project-${pIdx}-image-0`}
                    onClick={() => openLightbox(pIdx)}
                    className="group relative overflow-hidden rounded-2xl cursor-pointer hover-lift md:col-span-2 md:row-span-2"
                  >
                    <img
                      src={project.images[0]}
                      alt={`${project.title} ${project.location} 1`}
                      loading="lazy"
                      className="w-full h-[320px] md:h-[640px] object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--gold)]/95 text-[var(--black)] text-xs md:text-sm font-semibold uppercase tracking-widest shadow-lg">
                        <Expand size={16} />
                        Skatīt galeriju
                      </span>
                    </div>
                  </div>
                  {project.images.slice(1, 3).map((img, idx) => (
                    <div
                      key={idx}
                      data-testid={`project-${pIdx}-image-${idx + 1}`}
                      onClick={() => openLightbox(pIdx)}
                      className="group relative overflow-hidden rounded-2xl cursor-pointer hover-lift"
                    >
                      <img
                        src={img}
                        alt={`${project.title} ${project.location} ${idx + 2}`}
                        loading="lazy"
                        className="w-full h-[220px] md:h-[308px] object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                    </div>
                  ))}
                </div>
              ) : (
                /* 1-2 images: evenly sized */
                <div className={`grid ${project.images.length === 2 ? "md:grid-cols-2" : "md:grid-cols-1"} gap-4 md:gap-6`}>
                  {project.images.map((img, idx) => (
                    <div
                      key={idx}
                      data-testid={`project-${pIdx}-image-${idx}`}
                      onClick={() => openLightbox(pIdx)}
                      className="group relative overflow-hidden rounded-2xl cursor-pointer hover-lift"
                    >
                      <img
                        src={img}
                        alt={`${project.title} ${project.location} ${idx + 1}`}
                        loading="lazy"
                        className="w-full h-[360px] md:h-[560px] object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--gold)]/95 text-[var(--black)] text-xs md:text-sm font-semibold uppercase tracking-widest shadow-lg">
                          <Expand size={16} />
                          Skatīt galeriju
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <p className="text-[var(--gray-light)] mb-4">
            Vēlaties redzēt vairāk vai apspriest savu projektu?
          </p>
          <a href="#kontakti">
            <Button
              data-testid="projects-cta"
              className="bg-[var(--gold)] hover:bg-[var(--gold-light)] text-[var(--black)] font-semibold px-8 py-3 rounded-full transition-all duration-300"
            >
              Sazināties ar mums
            </Button>
          </a>
        </div>
      </div>

      {/* Lightbox - full gallery view */}
      {activeProject && (
        <div
          data-testid="lightbox"
          onClick={closeLightbox}
          className="fixed inset-0 z-[60] bg-black/95 overflow-y-auto animate-fade-in"
        >
          <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-black/80 backdrop-blur-md border-b border-[var(--gold)]/20">
            <div>
              <p className="text-[var(--gold)] text-xs uppercase tracking-[0.3em]">
                {activeProject.category}
              </p>
              <p className="text-white font-serif text-lg md:text-xl">
                {activeProject.title} — {activeProject.location}
              </p>
            </div>
            <button
              data-testid="lightbox-close"
              onClick={closeLightbox}
              className="text-white hover:text-[var(--gold)] transition-colors p-2"
              aria-label="Aizvērt"
            >
              <X size={28} />
            </button>
          </div>

          <article
            onClick={(e) => e.stopPropagation()}
            className="max-w-5xl mx-auto px-4 md:px-6 py-8"
            itemScope
            itemType="https://schema.org/CreativeWork"
          >
            {/* SEO-friendly project description */}
            <header className="mb-8 pb-8 border-b border-[var(--gold)]/20">
              <h2
                className="text-3xl md:text-4xl font-serif font-bold text-[var(--white)] mb-3"
                itemProp="name"
              >
                {activeProject.title} — {activeProject.location}
              </h2>
              <div className="flex flex-wrap items-center gap-4 mb-5 text-sm text-[var(--gray-light)]">
                <span className="flex items-center gap-2" itemProp="locationCreated">
                  <MapPin size={14} className="text-[var(--gold)]" />
                  {activeProject.location}, Latvija
                </span>
                <span className="text-[var(--gold)]/60">·</span>
                <span itemProp="genre">{activeProject.category}</span>
              </div>
              <p
                className="text-[var(--white-muted)] text-base md:text-lg leading-relaxed mb-5"
                itemProp="description"
              >
                {activeProject.description}
              </p>
              <div
                className="flex flex-wrap gap-2"
                itemProp="keywords"
                content={activeProject.keywords}
              >
                {activeProject.keywords.split(" · ").map((kw, i) => (
                  <span
                    key={kw}
                    className="text-xs uppercase tracking-wider text-[var(--gold)] border border-[var(--gold)]/40 px-3 py-1 rounded-full"
                  >
                    {kw}
                  </span>
                ))}
              </div>
            </header>

            {/* Gallery */}
            <div className="space-y-6" itemProp="image">
              {activeProject.images.map((img, idx) => (
                <figure
                  key={img}
                  data-testid={`lightbox-image-${idx}`}
                  className="relative rounded-2xl overflow-hidden bg-[var(--black-lighter)]"
                >
                  <img
                    src={img}
                    alt={`${activeProject.title} ${activeProject.location} — foto ${idx + 1} no ${activeProject.images.length}`}
                    className="w-full h-auto object-contain"
                  />
                  <figcaption className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-full px-3 py-1">
                    <span className="text-[var(--gold)] text-xs tracking-widest uppercase">
                      {idx + 1} / {activeProject.images.length}
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>

            <div className="text-center pt-8 pb-4 space-y-4">
              <p className="text-[var(--gray-light)] text-sm">
                Patīk šis projekts? Sazinieties ar mums, lai apspriestu jūsu nākamo projektu.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="#kontakti" onClick={closeLightbox}>
                  <Button
                    data-testid="lightbox-contact-cta"
                    className="bg-[var(--gold)] hover:bg-[var(--gold-light)] text-[var(--black)] font-semibold px-6 py-3 rounded-full"
                  >
                    Sazināties ar mums
                  </Button>
                </a>
                <button
                  onClick={closeLightbox}
                  data-testid="lightbox-close-bottom"
                  className="text-[var(--gold)] hover:text-[var(--gold-light)] text-sm uppercase tracking-widest transition-colors"
                >
                  Aizvērt galeriju
                </button>
              </div>
            </div>
          </article>
        </div>
      )}
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast.success(response.data.message || "Ziņa veiksmīgi nosūtīta!");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Kļūda nosūtot ziņu. Lūdzu, mēģiniet vēlreiz.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Phone className="w-5 h-5" />,
      label: "Tālrunis",
      value: "+371 20 310 517",
      href: "tel:+37120310517",
    },
    {
      icon: <Mail className="w-5 h-5" />,
      label: "E-pasts",
      value: "krplanning@outlook.lv",
      href: "mailto:krplanning@outlook.lv",
    },
    {
      icon: <MapPin className="w-5 h-5" />,
      label: "Adrese",
      value: "Grūdupu iela 19, Mārupe, LV-2167",
      href: null,
    },
    {
      icon: <MessageCircle className="w-5 h-5" />,
      label: "WhatsApp",
      value: "Rakstīt WhatsApp",
      href: "https://wa.me/37120310517?text=Sveiki!%20Vēlos%20uzzināt%20vairāk%20par%20KR%20Planning%20pakalpojumiem.",
    },
  ];

  return (
    <section
      id="kontakti"
      data-testid="contact-section"
      className="section-padding bg-[var(--black)]"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[var(--gold)] text-sm uppercase tracking-[0.2em] mb-4">
            Kontakti
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-6">
            Sazinies ar <span className="gold-gradient-text">mums</span>
          </h2>
          <p className="text-[var(--white-muted)] text-base md:text-lg max-w-2xl mx-auto">
            Mēs esam gatavi atbildēt uz jūsu jautājumiem un palīdzēt 
            realizēt jūsu sapņu projektu
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Info */}
          <div className="space-y-6">
            <h3 className="text-2xl font-serif font-semibold text-[var(--white)]">
              Kontaktinformācija
            </h3>
            <div className="space-y-4">
              {contactInfo.map((item, index) => (
                item.href ? (
                  <a
                    key={index}
                    href={item.href}
                    target={item.label === "WhatsApp" ? "_blank" : undefined}
                    rel={item.label === "WhatsApp" ? "noopener noreferrer" : undefined}
                    data-testid={`contact-${item.label.toLowerCase()}`}
                    className="flex items-center gap-4 p-4 rounded-xl bg-[var(--black-lighter)] border border-[var(--gray-dark)] hover:border-[var(--gold)] transition-all duration-300 group"
                  >
                    <div className="w-12 h-12 rounded-full bg-[var(--gold)]/10 flex items-center justify-center text-[var(--gold)] group-hover:bg-[var(--gold)] group-hover:text-[var(--black)] transition-all duration-300">
                      {item.icon}
                    </div>
                    <div>
                      <p className="text-xs text-[var(--gray-light)] uppercase tracking-wider mb-1">
                        {item.label}
                      </p>
                      <p className="text-[var(--white)] font-medium">{item.value}</p>
                    </div>
                  </a>
                ) : (
                  <div
                    key={index}
                    data-testid={`contact-${item.label.toLowerCase()}`}
                    className="flex items-center gap-4 p-4 rounded-xl bg-[var(--black-lighter)] border border-[var(--gray-dark)]"
                  >
                    <div className="w-12 h-12 rounded-full bg-[var(--gold)]/10 flex items-center justify-center text-[var(--gold)]">
                      {item.icon}
                    </div>
                    <div>
                      <p className="text-xs text-[var(--gray-light)] uppercase tracking-wider mb-1">
                        {item.label}
                      </p>
                      <p className="text-[var(--white)] font-medium">{item.value}</p>
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <Card className="bg-[var(--black-lighter)] border-[var(--gray-dark)]">
              <CardContent className="p-8">
                <h3 className="text-2xl font-serif font-semibold mb-6 text-[var(--white)]">
                  Nosūtīt ziņu
                </h3>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[var(--white-muted)]">
                        Vārds *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="contact-name-input"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-[var(--gray-dark)] border-[var(--gray)] text-[var(--white)] placeholder:text-[var(--gray-light)] focus:border-[var(--gold)] focus:ring-[var(--gold)]"
                        placeholder="Jūsu vārds"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[var(--white-muted)]">
                        E-pasts *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="contact-email-input"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-[var(--gray-dark)] border-[var(--gray)] text-[var(--white)] placeholder:text-[var(--gray-light)] focus:border-[var(--gold)] focus:ring-[var(--gold)]"
                        placeholder="jusu@epasts.lv"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-[var(--white-muted)]">
                      Tālrunis
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      data-testid="contact-phone-input"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-[var(--gray-dark)] border-[var(--gray)] text-[var(--white)] placeholder:text-[var(--gray-light)] focus:border-[var(--gold)] focus:ring-[var(--gold)]"
                      placeholder="+371 xxxxxxxx"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[var(--white-muted)]">
                      Ziņa *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="contact-message-input"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="bg-[var(--gray-dark)] border-[var(--gray)] text-[var(--white)] placeholder:text-[var(--gray-light)] focus:border-[var(--gold)] focus:ring-[var(--gold)] resize-none"
                      placeholder="Aprakstiet savu projektu vai jautājumu..."
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={isSubmitting}
                    className="w-full bg-[var(--gold)] hover:bg-[var(--gold-light)] text-[var(--black)] font-semibold py-6 rounded-full transition-all duration-300 disabled:opacity-50"
                  >
                    {isSubmitting ? "Nosūta..." : "Nosūtīt ziņu"}
                    {!isSubmitting && <ArrowRight className="ml-2" size={18} />}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Company Details - Full Width Below */}
        <div className="mt-12 p-6 rounded-xl bg-[var(--black-lighter)] border border-[var(--gray-dark)]">
          <h4 className="text-lg font-semibold mb-6 text-[var(--gold)]">
            Uzņēmuma rekvizīti
          </h4>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 text-sm">
            <div>
              <p className="text-[var(--gray-light)] text-xs uppercase mb-2">Uzņēmums</p>
              <p className="text-[var(--white)] font-medium">KR Planning, SIA</p>
            </div>
            <div>
              <p className="text-[var(--gray-light)] text-xs uppercase mb-2">Juridiskā adrese</p>
              <p className="text-[var(--white-muted)]">Mazā Akotu iela 1 - 2, Alejas,</p>
              <p className="text-[var(--white-muted)]">Ķekavas pag., Ķekavas nov., LV-2111</p>
            </div>
            <div>
              <p className="text-[var(--gray-light)] text-xs uppercase mb-2">Reģ. Nr.</p>
              <p className="text-[var(--white-muted)]">40203723150</p>
            </div>
            <div>
              <p className="text-[var(--gray-light)] text-xs uppercase mb-2">Banka</p>
              <p className="text-[var(--white-muted)]">A/S Swedbank</p>
              <p className="text-[var(--white-muted)]">SWIFT/BIC: HABALV22</p>
              <p className="text-[var(--white-muted)]">Konta Nr.: LV48HABA0551064369032</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-[var(--black-lighter)] py-8 border-t border-[var(--gray-dark)]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-col leading-none">
            <span className="text-2xl font-serif font-bold tracking-wide gold-gradient-text">
              KR
            </span>
            <span className="text-xs font-medium tracking-[0.35em] text-[var(--gold-dark)] uppercase">
              Planning
            </span>
          </div>
          <p className="text-[var(--gray-light)] text-sm text-center">
            © {currentYear} KR Planning, SIA. Visas tiesības aizsargātas.
          </p>
          <div className="flex items-center gap-4">
            <a
              href="tel:+37120310517"
              className="text-[var(--gray-light)] hover:text-[var(--gold)] transition-colors"
            >
              <Phone size={20} />
            </a>
            <a
              href="mailto:krplanning@outlook.lv"
              className="text-[var(--gray-light)] hover:text-[var(--gold)] transition-colors"
            >
              <Mail size={20} />
            </a>
            <a
              href="https://wa.me/37120310517"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--gray-light)] hover:text-[var(--gold)] transition-colors"
            >
              <MessageCircle size={20} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

const TOASTER_OPTIONS = {
  style: {
    background: 'var(--black-lighter)',
    color: 'var(--white)',
    border: '1px solid var(--gold)',
  },
};

// Main App
function App() {
  const [activeSection, setActiveSection] = useState("hero");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["hero", "par-mums", "pakalpojumi", "projekti", "kontakti"];
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="App">
      <Toaster position="top-right" toastOptions={TOASTER_OPTIONS} />
      <Navigation activeSection={activeSection} />
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <ProjectsSection />
      <ContactSection />
      <Footer />
    </div>
  );
}

export default App;
